class KinderlyAnimations {
  constructor() {
    this.parallaxData = [];
    this.initObservers();
    
    const defer = window.requestIdleCallback ? requestIdleCallback : (cb) => setTimeout(cb, 200);
    defer(() => {
      this.initSmoothScroll();
      this.bindEvents();
      this.initParallax();
    });
  }

  initObservers() {
    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('[data-saos], [data-card-animate], .section-reveal, .zigzag-animate-item').forEach(el => {
        el.classList.add('is-animated');
        el.style.setProperty('--saos-duration', '0s');
        el.style.setProperty('--saos-delay', '0s');
      });
      return;
    }

    if (!this.saosObserver) {
      this.saosObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const duration = el.dataset.duration || '1s';
            const delay = el.dataset.delay || '0s';
            
            el.style.setProperty('--saos-duration', duration.includes('s') ? duration : duration + 's');
            el.style.setProperty('--saos-delay', delay.includes('s') ? delay : delay + 's');
            
            if (el.getAttribute('data-saos') === 'layer-reveal') {
              this.handleLayerReveal(el);
            } else {
              el.classList.add('is-animated');
            }
            
            this.saosObserver.unobserve(el);
          }
        });
      }, { threshold: 0.05, rootMargin: '0px 0px -50px 0px' });
    }

    document.querySelectorAll('[data-saos]:not(.is-observed), .section-reveal:not(.is-observed)').forEach(el => {
      el.classList.add('is-observed');
      this.saosObserver.observe(el);
    });

    if (!this.cardObserver) {
      this.cardObserver = new IntersectionObserver((entries) => {
        const visibleCards = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => {
            const rectA = a.boundingClientRect;
            const rectB = b.boundingClientRect;
            return rectA.top - rectB.top || rectA.left - rectB.left;
          })
          .map(entry => entry.target);

        if (visibleCards.length > 0) {
          visibleCards.forEach((card, index) => {
            const delay = index * 0.1;
            card.style.setProperty('--saos-delay', delay + 's');
            card.classList.add('is-animated');
            this.cardObserver.unobserve(card);
          });
        }
      }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' });
    }

    document.querySelectorAll('[data-card-animate]:not(.is-observed)').forEach(el => {
      el.classList.add('is-observed');
      this.cardObserver.observe(el);
    });
  }

  handleLayerReveal(element) {
    if (element.classList.contains('is-animated')) return;
    
    const duration = parseFloat(element.dataset.duration) || 1;
    const delay = parseFloat(element.dataset.delay) || 0;
    
    const mask = document.createElement('div');
    mask.className = 'reveal-mask';
    mask.style.cssText = `position:absolute;top:0;left:0;width:100%;height:100%;background:white;z-index:10;transform-origin:left;transition: transform ${duration}s cubic-bezier(0.7, 0, 0.3, 1) ${delay}s;`;
    
    element.style.position = 'relative';
    element.appendChild(mask);
    
    requestAnimationFrame(() => {
      element.classList.add('is-animated');
      mask.style.transform = 'scaleX(0)';
      setTimeout(() => {
        mask.remove();
      }, (duration + delay) * 1000 + 100);
    });
  }

  initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]:not(.parallax-initialized)');
    if (parallaxElements.length === 0 && this.parallaxData.length > 0) return;

    const newParallaxData = Array.from(parallaxElements).map(el => {
      const rect = el.getBoundingClientRect();
      return {
        el,
        speed: parseFloat(el.dataset.parallax) || 0.1,
        top: rect.top + window.scrollY,
        height: rect.height,
        inView: false
      };
    });

    newParallaxData.forEach(data => data.el.classList.add('parallax-initialized'));

    this.parallaxData = [...(this.parallaxData || []), ...newParallaxData];

    if (!this.parallaxObserver) {
      this.parallaxObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const data = this.parallaxData.find(d => d.el === entry.target);
          if (data) data.inView = entry.isIntersecting;
        });
      }, { threshold: 0, rootMargin: '20% 0px' });
    }

    parallaxElements.forEach(el => this.parallaxObserver.observe(el));

    if (!this.scrollListenerAttached) {
      const handleParallax = () => {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const centerViewport = scrollY + viewportHeight / 2;

        this.parallaxData.forEach(data => {
          if (!data.inView) return;

          const relativeScroll = centerViewport - (data.top + data.height / 2);
          const yPos = relativeScroll * data.speed;
          data.el.style.transform = `translate3d(0, ${yPos.toFixed(2)}px, 0)`;
        });
      };

      if (window.lenis) {
        window.lenis.on('scroll', handleParallax);
      } else {
        window.addEventListener('scroll', handleParallax, { passive: true });
      }
      this.scrollListenerAttached = true;
      handleParallax();
    }
  }

  initSmoothScroll() {
    if (window.Shopify?.designMode) return;
    if (typeof Lenis === 'undefined') return;

    this.lenis = new Lenis({
      lerp: 0.1,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      smoothHover: true,
      smoothTouch: false,
    });

    const raf = (time) => {
      this.lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    this.initObserver();
    this.initPreventAuto();
    this.initAnchorLinks();

    window.lenis = this.lenis;
    document.documentElement.classList.add('lenis-enabled');
  }

  initAnchorLinks() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#' || href.startsWith('##')) return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          this.lenis.scrollTo(target, {
            offset: 0,
            duration: 1.5,
          });
        }
      });
    });
  }

  initObserver() {
    const observer = new MutationObserver(() => {
      const bodyClass = document.body.className;
      const isStopScroll =
        bodyClass.includes('drawer-open') ||
        bodyClass.includes('quickview-open') ||
        bodyClass.includes('active-search') ||
        bodyClass.includes('modal-open') ||
        bodyClass.includes('no-scroll') ||
        bodyClass.includes('pickup-sidebar-open') ||
        document.body.style.overflow === 'hidden';

      if (isStopScroll) {
        if (!this.lenis.isStopped) this.lenis.stop();
      } else {
        if (this.lenis.isStopped) this.lenis.start();
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  initPreventAuto() {
    const selectors = ['.cart-drawer', '.quickview-container', '.search-container', '.drawer-navigation', '.mobile-facets'];
    const applyPrevent = (root = document) => {
      selectors.forEach(selector => {
        const nodes = root.nodeType === 1 && root.matches(selector) ? [root] : root.querySelectorAll(selector);
        nodes.forEach(el => el.setAttribute('data-lenis-prevent', 'true'));
      });
    };
    applyPrevent();
    this.refreshPrevent = applyPrevent;
  }

  bindEvents() {
    document.addEventListener('shopify:section:load', (e) => {
      this.refresh();
      if (this.refreshPrevent) this.refreshPrevent(e.target);
    });
    document.addEventListener('shopify:section:select', () => this.refresh());
    document.addEventListener('cart:drawer-opened', () => this.refreshPrevent && this.refreshPrevent());
    document.addEventListener('quickview:opened', () => this.refreshPrevent && this.refreshPrevent());

    window.initSectionAnimations = (section) => {
      const secEl = typeof section === 'string' ? document.querySelector(section) : section;
      if (!secEl) return;

      if (window.Shopify && window.Shopify.designMode) {
        secEl.querySelectorAll('[data-saos], [data-card-animate], .section-reveal, .zigzag-animate-item').forEach(el => {
          el.classList.add('is-animated');
          el.style.setProperty('--saos-duration', '0s');
          el.style.setProperty('--saos-delay', '0s');
        });
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const duration = el.dataset.duration || '0.8s';
            const delay = el.dataset.delay || '0s';
            el.style.setProperty('--saos-duration', duration);
            el.style.setProperty('--saos-delay', delay);
            el.classList.add('is-animated');
            observer.unobserve(el);
          }
        });
      }, { threshold: 0.1 });

      const elements = secEl.querySelectorAll('[data-saos]:not(.is-animated), [data-card-animate]:not(.is-animated)');
      
      elements.forEach(el => observer.observe(el));
    };

    window.reinitAllAnimations = () => this.refresh();
  }

  refresh() {
    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('[data-saos], [data-card-animate], .section-reveal, .zigzag-animate-item').forEach(el => {
        el.classList.add('is-animated');
        el.style.setProperty('--saos-duration', '0s');
        el.style.setProperty('--saos-delay', '0s');
      });
      return;
    }
    document.querySelectorAll('[data-saos].is-animated, [data-card-animate].is-animated').forEach(el => {
      el.classList.remove('is-animated');
    });
    this.initObservers();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new KinderlyAnimations());
} else {
  new KinderlyAnimations();
}
